import React, { FC, useCallback, useEffect, useRef, useState } from 'react';

import Cytoscape, { Core } from 'cytoscape';
import COSEBilkent from 'cytoscape-cose-bilkent';
import CytoscapeComponent from 'react-cytoscapejs';

import styles from './ContextModelDiagram.module.scss';
import { ContextModel, PowerplantContextModel } from 'pages/ContextManagerPage/PowerplantContextModel';
import { graphStyleSheet, layout } from './cytoscapeConfig';

// Register the COSE-Bilkent layout
Cytoscape.use(COSEBilkent);

const nodeBackgroundColors: {[level: string]: string} = { /* Had to add this type to avoid error when indexing with strings */
  Component: '#F16667',
  powerplant: '#FFC454',
  SubSystem: '#57C7E3',
  System: '#F79767',
}

interface ContextModelDiagramProps {
  powerplantModel: PowerplantContextModel;
  powerplantName: string;
  onUnselectAll?: () => void;
  onSelectEdge?: (edgeData: any) => void;
  onSelectNode?: (nodeData: any) => void;
}

const ContextModelDiagram: FC<ContextModelDiagramProps> = (
  {
    powerplantModel,
    powerplantName,
    onUnselectAll,
    onSelectEdge,
    onSelectNode,
  },
) => {

  const [cytoscapeElements, setCytoscapeElements] = useState<Cytoscape.ElementDefinition[]>([])
  const cytoscapeRef = useRef<Cytoscape.Core | null>(null);

  useEffect(() => {
    if (powerplantModel.systems) {
      const elements = createCytoscapeElements(powerplantModel.systems)
      setCytoscapeElements(elements);
    }
  }, [powerplantModel]);

  useEffect(() => {
    const cy = cytoscapeRef.current;
    if (!cy) return;

    const allUnselectedHandler = (): void => {
      onUnselectAll?.();
    };

    const edgeSelectedHandler = (event: Cytoscape.EventObject): void => {
      const edgeData = event.target.json().data;
      onSelectEdge?.(edgeData);
    };

    const nodeSelectedHandler = (event: Cytoscape.EventObject): void => {
      const nodeData = event.target.json().data;
      onSelectNode?.(nodeData);
    };

    // Attach event listeners
    cy.on('select', 'node', nodeSelectedHandler);
    cy.on('select', 'edge', edgeSelectedHandler);
    cy.on('select', 'node, edge', e => cy.elements().not(e.target).unselect());
    cy.on('unselect', allUnselectedHandler);

    const cleanup = () => {
      cy.off('select', 'node', nodeSelectedHandler);
      cy.off('select', 'edge', edgeSelectedHandler);
      cy.off('select', 'node, edge');
      cy.off('unselect');
    };

    return cleanup;
  }, [cytoscapeRef, onUnselectAll, onSelectEdge, onSelectNode]);

  const cytoscapeReferenceHandler = useCallback((cy: Core): void => {
    cytoscapeRef.current = cy;
  }, []);

  const createCytoscapeElements = (systems: Record<string, ContextModel.System>): Cytoscape.ElementDefinition[] => {
    const result: Cytoscape.ElementDefinition[] = [];

    const powerplantNodeData: object = { label: powerplantName, id: powerplantName, size: 100 };
    const powerplantNodeStyle: object = { 'background-color': nodeBackgroundColors.powerplant };
    result.push({ data: powerplantNodeData, style: powerplantNodeStyle });

    for (const key in systems) {
      extractNodeData(systems[key], result);
      extractEdgeData(systems[key], result, powerplantName);
    }

    return result;
  }

  const extractEdgeData = (item: ContextModel.Item, result: any[], parentId?: string): void => {
    if (item.code && parentId) {
      const edgeData: any = {
        source: parentId,
        target: item.code,
        label: `Edge from ${parentId} to ${item.code}`,
      };
      result.push({ data: edgeData });
    }

    if (item.children) {
      if (typeof item.children === 'object') {
        for (const key in item.children) {
          extractEdgeData(item.children[key], result, item.code);
        }
      } else {
        extractEdgeData(item.children, result, item.code);
      }
    }
  }

  const extractNodeData = (item: ContextModel.Item, result: any[]): void => {
    const nodeData: any = { label: item.name, id: item.code, size: 100 };
    const nodeStyle: object = { 'background-color': nodeBackgroundColors[item.level] || ''};

    if (item.name) {
      result.push({ data: nodeData, style: nodeStyle });
    }

    if (item.children) {
      if (typeof item.children === 'object') {
        for (const key in item.children) {
          extractNodeData(item.children[key], result);
        }
      } else {
        extractNodeData(item.children, result);
      }
    }
  }

  return (
    <div
      className={`${styles.ContextModelDiagram} project-shadow-border`}
      data-testid="ContextModelDiagram"
    >
      {cytoscapeElements && !!cytoscapeElements.length &&
        <CytoscapeComponent
          className={styles.graphStyles}
          cy={cytoscapeReferenceHandler}
          elements={cytoscapeElements}
          layout={layout}
          stylesheet={graphStyleSheet}
          wheelSensitivity={0.1}
        />
      }

      { !cytoscapeElements || !cytoscapeElements.length &&
        <div className={styles.noModel}>No model</div>
      }
    </div>
  );
}

export default ContextModelDiagram;
